import { useSubscriptionSplitScreen } from 'modules/dashboard/pages/SubscriptionSplitScreen/hooks/useSubscriptionSplitScreen';
import ReactDatePicker from 'react-datepicker';
import { Controller } from 'react-hook-form';
import { OPERATION_MODES } from 'services/api/getSubscriptionRevenueData';
import { Button } from 'shared/Components/Button';
import { Input } from 'shared/Components/Input';
import { LoadingOverlay } from 'shared/Components/LoadingOverlay/LoadingOverlay';
import { SelectForm } from 'shared/Components/Select';
import { Splash } from 'shared/Components/Splash';
import { ErrorMessage, RelativeInputDiv } from 'shared/Typhography';
import { Box } from 'shared/Typhography/Box';
import { FlexContainer } from 'shared/Typhography/FlexContainer';
import { Label } from 'shared/Typhography/InputStyled';
import { LinkButton } from 'shared/Typhography/LinkButton';
import { theme } from 'theme';

/**
 *
 * @todo - Implement detection of previously processed months.
 */
export const SubscriptionSplitScreen = () => {
  const {
    form,
    screenState,
    screenErrors,
    previousMonthDateUTC,
    projectOptions,
    reportUrl,
    renderMonthContent,
    submit,
  } = useSubscriptionSplitScreen();

  return (
    <Box width="500px" margin="150px auto">
      {screenErrors.length > 0 ? (
        <div>
          <p>
            Error occurred while trying to fetch page data: <br />
          </p>

          <code
            style={{
              display: 'block',
              padding: '16px',
              marginTop: '32px',
              border: `1px solid ${theme.colors.red}`,
            }}
          >
            {screenErrors.map((error, index) =>
              typeof error === 'string' ? (
                <pre key={index}>{JSON.stringify(JSON.parse(error), null, 2)}</pre>
              ) : (
                <pre key={index}>{JSON.stringify(error, null, 2)}</pre>
              ),
            )}
          </code>
        </div>
      ) : undefined}

      <h1 style={{ fontSize: '1.5rem', marginBottom: '2rem' }}>Subscription revenue split</h1>

      <LoadingOverlay isLoading={screenState !== 'awaiting-input'}>
        <form onSubmit={submit}>
          <FlexContainer flexDirection="column" gap="2.5rem">
            <Controller
              name="date"
              control={form.control}
              render={(props) => {
                return (
                  <RelativeInputDiv>
                    <Label htmlFor="month">Month</Label>
                    <ReactDatePicker
                      id="month"
                      calendarClassName="subscription-split_month-picker"
                      onChange={props.field.onChange}
                      selected={props.field.value}
                      maxDate={previousMonthDateUTC}
                      renderMonthContent={renderMonthContent}
                      inline
                      showMonthYearPicker
                    />
                    <ErrorMessage>{form.formState.errors.date?.message}</ErrorMessage>
                  </RelativeInputDiv>
                );
              }}
            />

            <SelectForm
              id="project"
              name="project"
              control={form.control}
              values={projectOptions}
              label="Select project"
              error={form.formState.errors.project}
              style={{ width: '100%' }}
            />

            <Input
              control={form.control}
              id="amount"
              name="amount"
              placeholder="Amount"
              type="number"
              label="Amount"
              error={form.formState.errors.amount}
              width="300px"
            />

            <fieldset style={{ paddingTop: '16px', paddingBottom: '16px' }}>
              <legend>Mode of execution</legend>
              <RelativeInputDiv>
                <FlexContainer flexDirection="column" gap="10px">
                  <FlexContainer gap="10px">
                    <Input
                      control={form.control}
                      id="mode_process"
                      name="mode"
                      type="radio"
                      width="25px"
                      defaultValue={OPERATION_MODES.executeAndReport}
                    />
                    <label htmlFor="mode_process">Execute and report</label>
                  </FlexContainer>
                  <FlexContainer gap="10px">
                    <Input
                      control={form.control}
                      id="mode_report"
                      name="mode"
                      type="radio"
                      width="25px"
                      defaultValue={OPERATION_MODES.reportOnly}
                    />
                    <label htmlFor="mode_report">Report only</label>
                  </FlexContainer>
                  <FlexContainer gap="10px">
                    <Input
                      control={form.control}
                      id="mode_force"
                      name="mode"
                      type="radio"
                      width="25px"
                      defaultValue={OPERATION_MODES.executeAndReportForce}
                    />
                    <label htmlFor="mode_force">Execute and report (force)</label>
                  </FlexContainer>
                  <ErrorMessage>{form.formState.errors.mode?.message}</ErrorMessage>
                </FlexContainer>
              </RelativeInputDiv>
            </fieldset>

            <FlexContainer gap="10px" flexDirection="column">
              <Button type="submit" height={40} disabled={screenState !== 'awaiting-input'}>
                {screenState === 'submitting' ? <Splash color="white" width={30} /> : 'Submit'}
              </Button>

              {reportUrl && (
                <LinkButton
                  href={reportUrl}
                  style={{ height: 40, fontWeight: 'bold', fontSize: '13.333px' }}
                  download
                >
                  📋 Download report
                </LinkButton>
              )}
            </FlexContainer>
          </FlexContainer>
        </form>
      </LoadingOverlay>
    </Box>
  );
};
